// Imports
import { faPrint } from '@fortawesome/pro-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import MultiFieldContainer from '../../../../../../shareables/foundation/front-end/components/forms/multi-field-container';
import { visuallyHiddenLabelStyle } from '../../../../shared-components/visually-hidden-label';
import { useAppDispatch, useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import usePagination from '../../../../../../shareables/foundation/front-end/utils/use-pagination';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import planMapping from '../../../../../../shareables/utils/billing/plan-mapping';
import { GetAccountsResponse } from '../../../../../../shareables/types/api/main/website/page/type/207/accounts/get';
import UserControlButton from '../shared-components/user-control-button';
import Account from './account';
import PrintableDirectory from './printable-directory';


// Styled components
const SearchAndPageSizeWrapper = styled(MultiFieldContainer)`
	label {
		${visuallyHiddenLabelStyle}
	}
	
	margin-bottom: 0.8438rem;
`;

const Heading = styled.h3`
	display: block;
	font-size: 1.5rem;
	margin-top: 1.25rem;
	margin-bottom: 0.5313rem;
	font-weight: 500;
	line-height: 1.1;
`;


// Define the accepted props
interface Props {
	pageID: number;
	pageHeader: string;
	storybookAccounts?: GetAccountsResponse;
	storybookSearchQuery?: string;
}


// Function component
const ResidentDirectory: React.FC<Props> = ({ pageID, pageHeader, storybookAccounts, storybookSearchQuery }) => {
	// Use state
	const [allAccounts, setAllAccounts] = useState<GetAccountsResponse | null>(storybookAccounts ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const communityName = useAppSelector((state) => state.self.website?.communityName);
	const websitePlan = useAppSelector((state) => state.self.website?.plan);
	
	
	// Create search field
	const {
		items: filteredAccounts,
		unslicedItems: allFilteredAccounts,
		setItems: setFilteredAccounts,
		searchQuery,
		searchField,
		pageSizeField,
		paginationComponent,
	} = usePagination<GetAccountsResponse[0]>({
		cacheStorageKey: `resident-directory-${pageID}-accounts`,
		pluralDescriptor: 'accounts',
		pageSizes: [50, 100, 250, 500],
		searchableKeys: [
			(account) => account.addresses.map((address) => address.formatted).join(' ') || 'no addresses',
			(account) => account.consolidatedName,
		],
		initialItems: storybookAccounts,
		initialSearchQuery: storybookSearchQuery,
	});
	
	
	// Load accounts on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetAccountsResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/207/accounts`,
				completion: (json) => {
					setAllAccounts(json.response);
					setFilteredAccounts(json.response);
				},
			})
		);
	}, [dispatch, pageID, setFilteredAccounts]);
	
	
	// If loading, display nothing
	if (allAccounts === null) {
		return null;
	}
	
	
	// Add placeholder to search field
	const searchFieldWithPlaceholder = React.cloneElement(searchField, { placeholder: 'Search residents…' });
	
	
	// Determine what a paper copy would contain
	//  -> Printing is an Engage feature, and the printed copy covers every household the search matched rather than just the page being viewed
	const printableAccounts =
		(websitePlan ?? 0) >= planMapping.engage && allFilteredAccounts !== null ? allFilteredAccounts : [];
	
	
	// Return JSX
	return (
		<React.Fragment>
			{printableAccounts.length > 0 && (
				<React.Fragment>
					<UserControlButton text='Print this directory' opened={false} onClick={() => window.print()} icon={faPrint} />
					
					<PrintableDirectory
						accounts={printableAccounts}
						communityName={communityName ?? ''}
						pageHeader={pageHeader}
						searchQuery={searchQuery}
					/>
				</React.Fragment>
			)}
			
			<SearchAndPageSizeWrapper centerOnMobile grow={1} moreSpaceBetween>
				{searchFieldWithPlaceholder}
				{pageSizeField}
			</SearchAndPageSizeWrapper>
			
			{allAccounts.length === 0 && <em>No accounts have chosen to be listed in the directory yet…</em>}
			
			{searchQuery.length > 0 && allAccounts.length > 0 && (
				<React.Fragment>
					<Heading>Showing results for “{searchQuery}”</Heading>
					{filteredAccounts?.length === 0 && <em>No results found…</em>}
				</React.Fragment>
			)}
			
			{filteredAccounts?.map((account, i) => <Account key={i} account={account} />)}
			
			{paginationComponent}
		</React.Fragment>
	);
};

export default ResidentDirectory;
